import React from 'react';
import { X, Clock, Users, BookOpen, Calculator } from 'lucide-react';
import { clsx } from 'clsx';
import MathRenderer from '../math/MathRenderer';

const AssessmentPreview = ({ assessment, onClose }) => {
  if (!assessment) return null;

  const getTotalPoints = () => {
    return assessment.questions?.reduce((total, question) => total + (question.points || 0), 0) || 0;
  };

  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case 'math': return Calculator;
      case 'multiple-choice': return BookOpen;
      case 'true-false': return BookOpen;
      default: return BookOpen;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Assessment Preview</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Assessment Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {assessment.title || 'Untitled Assessment'}
            </h1>
            {assessment.description && (
              <p className="text-gray-600 mb-4">{assessment.description}</p>
            )}
            
            {/* Assessment Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <BookOpen className="w-5 h-5 text-gray-400 mr-2" />
                <div>
                  <div className="text-sm font-medium text-gray-900">Questions</div>
                  <div className="text-sm text-gray-600">
                    {assessment.questions?.length || 0} questions
                  </div>
                </div>
              </div>
              
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-gray-400 mr-2" />
                <div>
                  <div className="text-sm font-medium text-gray-900">Time Limit</div>
                  <div className="text-sm text-gray-600">
                    {assessment.timeLimit ? `${assessment.timeLimit} minutes` : 'No limit'}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center">
                <Users className="w-5 h-5 text-gray-400 mr-2" />
                <div>
                  <div className="text-sm font-medium text-gray-900">Total Points</div>
                  <div className="text-sm text-gray-600">{getTotalPoints()} points</div>
                </div>
              </div>
            </div>
          </div>

          {/* Questions */}
          {assessment.questions && assessment.questions.length > 0 ? (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Questions</h2>
              
              {assessment.questions.map((question, index) => {
                const QuestionIcon = getQuestionTypeIcon(question.type);
                
                return (
                  <div key={question.id} className="border border-gray-200 rounded-lg p-6">
                    {/* Question Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-sigma-100 rounded-full">
                          <span className="text-sm font-medium text-sigma-700">{index + 1}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <QuestionIcon className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-500 capitalize">
                            {question.type.replace('-', ' ')}
                          </span>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500">
                        {question.points} {question.points === 1 ? 'point' : 'points'}
                      </span>
                    </div>

                    {/* Question Content */}
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">{question.title}</h4>
                      
                      {question.type === 'math' && question.mathContent ? (
                        <div className="p-4 bg-gray-50 rounded-lg text-center">
                          <MathRenderer latex={question.mathContent} />
                        </div>
                      ) : question.content && (
                        <p className="text-gray-700 whitespace-pre-wrap">{question.content}</p>
                      )}
                    </div>

                    {/* Answer Options */}
                    {question.type === 'multiple-choice' && question.options && (
                      <div className="space-y-2">
                        {question.options.map((option, optionIndex) => (
                          <div
                            key={optionIndex}
                            className={clsx(
                              'flex items-center p-3 rounded border',
                              question.correctAnswer == optionIndex
                                ? 'bg-green-50 border-green-200'
                                : 'bg-gray-50 border-gray-200'
                            )}
                          >
                            <span className="font-medium mr-2">
                              {String.fromCharCode(65 + optionIndex)}.
                            </span>
                            <span>{option}</span>
                            {question.correctAnswer == optionIndex && (
                              <span className="ml-auto text-green-600 text-xs font-medium">
                                Correct Answer
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {question.type === 'true-false' && (
                      <div className="space-y-2">
                        {['true', 'false'].map((option) => (
                          <div
                            key={option}
                            className={clsx(
                              'flex items-center p-3 rounded border',
                              question.correctAnswer === option
                                ? 'bg-green-50 border-green-200'
                                : 'bg-gray-50 border-gray-200'
                            )}
                          >
                            <span className="capitalize">{option}</span>
                            {question.correctAnswer === option && (
                              <span className="ml-auto text-green-600 text-xs font-medium">
                                Correct Answer
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Explanation */}
                    {question.explanation && (
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                        <div className="text-xs font-medium text-blue-800 mb-1">Explanation:</div>
                        <div className="text-sm text-blue-700">{question.explanation}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Questions</h3>
              <p className="text-gray-500">This assessment doesn't have any questions yet.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-sigma-600 text-white rounded-lg hover:bg-sigma-700 transition-colors"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentPreview;